// ── Contractor Telemetry Sink (P11b) ──
// Read-only, append-only, in-memory event log.
//
// Hard rules:
//   • No PII (no names, addresses, emails, phones).
//   • No customer-revenue / pricing data.
//   • Append-only. No mutation of past entries.
//   • Throttle: dedupe identical (eventType, contractorId, metaHash) within 5s.
//   • Never throws. Failures are silent — telemetry MUST NOT block UI.
//   • Separate from FrictionLog (which is for human-reported friction).

export type ContractorTelemetryEventType =
  | "anchor_view"
  | "tab_open"
  | "offer_accepted"
  | "offer_declined"
  | "manual_refresh";

export interface ContractorTelemetryEntry {
  eventType: ContractorTelemetryEventType;
  contractorId: string;
  timestamp: string;
  metadata: Record<string, unknown>;
  /**
   * P12.2: True when this event was emitted by a SuperAdmin Observer
   * session (read-only impersonation). Real metrics MUST exclude these.
   */
  observer?: boolean;
}

const THROTTLE_WINDOW_MS = 5000;
const MAX_LOG = 500;

const log: ContractorTelemetryEntry[] = [];
const lastSeen: Map<string, number> = new Map();

function metaHash(meta: Record<string, unknown>): string {
  try {
    return JSON.stringify(meta);
  } catch {
    return "";
  }
}

export interface LogTelemetryOptions {
  /** When true, marks the entry as a SuperAdmin Observer event. */
  observer?: boolean;
}

export function logContractorTelemetry(
  eventType: ContractorTelemetryEventType,
  contractorId: string,
  metadata: Record<string, unknown> = {},
  options: LogTelemetryOptions = {},
): void {
  try {
    if (!contractorId) return;
    const observer = options.observer === true;
    const key = `${eventType}::${contractorId}::${observer ? "obs" : "real"}::${metaHash(metadata)}`;
    const now = Date.now();
    const prev = lastSeen.get(key);
    if (prev != null && now - prev < THROTTLE_WINDOW_MS) return;
    lastSeen.set(key, now);

    const entry: ContractorTelemetryEntry = {
      eventType,
      contractorId,
      timestamp: new Date(now).toISOString(),
      metadata,
      observer,
    };
    log.push(entry);
    if (log.length > MAX_LOG) log.shift();
  } catch {
    /* fail silently */
  }
}

export function getContractorTelemetry(): ReadonlyArray<ContractorTelemetryEntry> {
  return log;
}

/** Real telemetry only — excludes SuperAdmin observer-tagged entries. */
export function getRealContractorTelemetry(): ReadonlyArray<ContractorTelemetryEntry> {
  return log.filter((e) => e.observer !== true);
}

export function clearContractorTelemetry(): void {
  log.length = 0;
  lastSeen.clear();
}
